import { useEffect, useMemo, useState } from "react";
import { useAdminUsers } from "../hooks/useAdminUsers";
import { useTranslation } from "../i18n";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import UsageChart from "./UsageChart";
import PlatformStatsCard from "./PlatformStatsCard";

export const AdminPanel = () => {
  const { t } = useTranslation();
  const {
    filteredUsers,
    selectedUser,
    search,
    isLoading,
    error,
    usageLimit,
    usageEvents,
    selectUser,
    setSearch,
    refreshUsers,
    createUser,
    setAdminStatus,
    updateUsageLimit,
    refreshUsageEvents,
  } = useAdminUsers();

  const [createOpen, setCreateOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newVerified, setNewVerified] = useState(false);
  const [savingLimit, setSavingLimit] = useState(false);
  const [rangeDays, setRangeDays] = useState(30);

  const [monthlyBudget, setMonthlyBudget] = useState("");
  const [tier, setTier] = useState("");
  const [paygEnabled, setPaygEnabled] = useState(false);

  useEffect(() => {
    if (usageLimit.data) {
      const budget =
        usageLimit.data.monthly_budget_usd == null
          ? ""
          : String(usageLimit.data.monthly_budget_usd);
      setMonthlyBudget(budget);
      setTier(usageLimit.data.tier || "");
      setPaygEnabled(Boolean(usageLimit.data.payg_enabled));
    } else {
      setMonthlyBudget("");
      setTier("");
      setPaygEnabled(false);
    }
  }, [usageLimit.data, selectedUser?.id]);

  useEffect(() => {
    if (!selectedUser) return;
    const endAt = Math.floor(Date.now() / 1000);
    const startAt = endAt - rangeDays * 24 * 60 * 60;
    refreshUsageEvents(selectedUser.id, startAt, endAt);
  }, [selectedUser?.id, rangeDays, refreshUsageEvents]);

  const selectedMeta = useMemo(() => {
    if (!selectedUser) return null;
    const label = selectedUser.display_name || selectedUser.email;
    return { label };
  }, [selectedUser]);

  const handleCreate = async () => {
    if (!newEmail.trim()) return;
    await createUser({
      email: newEmail.trim(),
      display_name: newDisplayName.trim() || null,
      verified: newVerified,
    });
    setNewEmail("");
    setNewDisplayName("");
    setNewVerified(false);
    setCreateOpen(false);
  };

  const handleUsageSave = async () => {
    if (!selectedUser) return;
    let budgetValue: number | null = null;
    if (monthlyBudget.trim()) {
      const parsed = Number(monthlyBudget);
      if (!Number.isFinite(parsed)) return;
      budgetValue = parsed;
    }
    setSavingLimit(true);
    try {
      await updateUsageLimit(selectedUser.id, {
        monthly_budget_usd: budgetValue,
        tier: tier.trim() || null,
        payg_enabled: paygEnabled,
      });
    } finally {
      setSavingLimit(false);
    }
  };


  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">
            {t("admin.title", "Admin")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("admin.subtitle", "Manage users and usage limits")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={refreshUsers} disabled={isLoading}>
            {isLoading
              ? t("admin.refreshing", "Refreshing...")
              : t("admin.refresh", "Refresh")}
          </Button>
          <AlertDialog open={createOpen} onOpenChange={setCreateOpen}>
            <AlertDialogTrigger asChild>
              <Button>{t("admin.create_user", "Create user")}</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("admin.create_title", "Create user")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    "admin.create_description",
                    "Add a user by email. Admin status must be granted separately."
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="admin-email">
                    {t("admin.email", "Email")}
                  </Label>
                  <Input
                    id="admin-email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="user@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-name">
                    {t("admin.display_name", "Display name")}
                  </Label>
                  <Input
                    id="admin-name"
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    placeholder={t("admin.optional", "Optional")}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={newVerified}
                    onCheckedChange={(checked) => setNewVerified(Boolean(checked))}
                  />
                  <Label>{t("admin.verified", "Verified")}</Label>
                </div>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>
                  {t("admin.cancel", "Cancel")}
                </AlertDialogCancel>
                <AlertDialogAction onClick={handleCreate}>
                  {t("admin.create", "Create")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {error && (
        <div className="text-sm text-destructive">{error}</div>
      )}

      <PlatformStatsCard />

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("admin.users_title", "Users")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("admin.search", "Search by email or name")}
            />
            <div className="space-y-2 max-h-[520px] overflow-auto pr-1">
              {filteredUsers.length === 0 && (
                <div className="text-sm text-muted-foreground">
                  {t("admin.no_users", "No users found")}
                </div>
              )}
              {filteredUsers.map((user) => {
                const isSelected = user.id === selectedUser?.id;
                return (
                  <button
                    key={user.id}
                    onClick={() => selectUser(user.id)}
                    className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${
                      isSelected
                        ? "border-primary/60 bg-primary/10"
                        : "border-border hover:bg-accent"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {user.display_name || user.email}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        {user.is_admin ? (
                          <Badge variant="success">
                            {t("admin.admin_badge", "Admin")}
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            {t("admin.user_badge", "User")}
                          </Badge>
                        )}
                        {user.verified ? (
                          <Badge variant="secondary">
                            {t("admin.verified", "Verified")}
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {selectedMeta
                ? selectedMeta.label
                : t("admin.select_user", "Select a user")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {!selectedUser && (
              <div className="text-sm text-muted-foreground">
                {t("admin.select_user_help", "Choose a user to view details.")}
              </div>
            )}
            {selectedUser && (
              <>
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">
                    {t("admin.email", "Email")}
                  </div>
                  <div className="text-sm font-medium">{selectedUser.email}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">
                    {t("admin.user_id", "User ID")}
                  </div>
                  <div className="text-xs text-muted-foreground break-all">
                    {selectedUser.id}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={selectedUser.is_admin ? "success" : "outline"}>
                    {selectedUser.is_admin
                      ? t("admin.admin_badge", "Admin")
                      : t("admin.standard_badge", "Standard")}
                  </Badge>
                  {selectedUser.verified ? (
                    <Badge variant="secondary">
                      {t("admin.verified", "Verified")}
                    </Badge>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={selectedUser.is_admin ? "outline" : "default"}
                    onClick={() =>
                      setAdminStatus(selectedUser.id, !selectedUser.is_admin)
                    }
                  >
                    {selectedUser.is_admin
                      ? t("admin.demote", "Demote admin")
                      : t("admin.promote", "Promote admin")}
                  </Button>
                </div>

                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">
                        {t("admin.usage_limits", "Usage limits")}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t(
                          "admin.usage_limits_hint",
                          "Admins ignore usage limits"
                        )}
                      </div>
                    </div>
                    {usageLimit.isLoading && (
                      <div className="text-xs text-muted-foreground">
                        {t("admin.loading_usage", "Loading...")}
                      </div>
                    )}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="limit-budget">
                        {t("admin.monthly_budget", "Monthly budget (USD)")}
                      </Label>
                      <Input
                        id="limit-budget"
                        value={monthlyBudget}
                        onChange={(e) => setMonthlyBudget(e.target.value)}
                        placeholder={t("admin.no_limit", "No limit")}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="limit-tier">
                        {t("admin.tier", "Tier")}
                      </Label>
                      <Input
                        id="limit-tier"
                        value={tier}
                        onChange={(e) => setTier(e.target.value)}
                        placeholder={t("admin.tier_placeholder", "basic / pro")}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={paygEnabled}
                      onCheckedChange={(checked) =>
                        setPaygEnabled(Boolean(checked))
                      }
                    />
                    <Label>{t("admin.payg", "Allow pay-as-you-go")}</Label>
                  </div>
                  {usageLimit.error && (
                    <div className="text-xs text-destructive">
                      {usageLimit.error}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      onClick={handleUsageSave}
                      disabled={savingLimit}
                    >
                      {savingLimit
                        ? t("admin.saving", "Saving...")
                        : t("admin.save_limits", "Save limits")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setMonthlyBudget("");
                        setTier("");
                        setPaygEnabled(false);
                      }}
                    >
                      {t("admin.clear", "Clear")}
                    </Button>
                  </div>
                </div>

                {selectedUser && (
                  <UsageChart
                    prefix="admin"
                    events={usageEvents.data}
                    isLoading={usageEvents.isLoading}
                    rangeDays={rangeDays}
                    onRangeDaysChange={setRangeDays}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPanel;
